import { Chip, ChipRow } from "../lib/Chip.tsx";
import { EmptyState } from "../lib/EmptyState.tsx";
import { PlacedRow } from "./PlacedRow.tsx";
import { SOLD_COPY } from "./soldWorks.constants.ts";
import { useSoldWorksController } from "./useSoldWorksController.ts";
import "./gallery.css";
import "./soldWorks.css";

/**
 * What this gallery has sold.
 *
 * The inventory's opposite number, and the screen that could not exist until
 * `ondo.transfers` did. Its `notYet` copy used to say so at length — "a screen
 * listing claims as sales would be reporting somebody's typing as a change of
 * ownership" — and every row here is now a transfer row rather than a claim.
 *
 * READ ONLY, and deliberately. Withdrawing a sale is a real act with a real
 * door (`POST /v1/transfers/:id/withdraw`), and it is not on this screen: it is
 * a correction that wants a confirmation and a reason, and a ledger is not the
 * place to put an undo button beside every line.
 *
 * It carries no title of its own: the portal header above it does.
 */
export function SoldWorks({ workBase }: { workBase: string }) {
  const {
    view,
    chooseArtist,
    confirmingId,
    working,
    askToWithdraw,
    cancelWithdraw,
    withdraw,
  } = useSoldWorksController(workBase);

  if (view.kind === "loading") {
    return (
      <p className="gallery__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="gallery__note gallery__note--error" role="alert">
        <h2 className="gallery__note-title">{view.title}</h2>
        <p>{view.message}</p>
      </section>
    );
  }

  if (view.kind === "empty") {
    return (
      <EmptyState className="gallery__empty" lead={view.message} detail={view.detail} />
    );
  }

  return (
    <>
      <p className="gallery__summary">{view.summary}</p>

      <section className="gallery__lead">
        <p>
          <strong>{SOLD_COPY.lead}</strong> {SOLD_COPY.leadAction}
        </p>
        {/* Says what the design's "full code history" would have implied and
            this cannot: a code is issued once and is never reissued. */}
        <p className="gallery__lead-note">{SOLD_COPY.codeNote}</p>
      </section>

      {view.artists.length > 0 && (
        <ChipRow label={SOLD_COPY.artistFilter}>
          <Chip pressed={view.artist === ""} onClick={() => chooseArtist("")}>
            {SOLD_COPY.allArtists}
          </Chip>
          {view.artists.map((each) => (
            <Chip
              key={each}
              pressed={view.artist === each}
              onClick={() => chooseArtist(each)}
            >
              {each}
            </Chip>
          ))}
        </ChipRow>
      )}

      {view.filteredOut ? (
        <p className="gallery__note">{SOLD_COPY.noMatches}</p>
      ) : (
        <ul className="gallery__rows">
          {view.rows.map((row) => (
            <PlacedRow
              key={row.transferId}
              row={row}
              confirming={confirmingId === row.transferId}
              busy={working === row.transferId}
              onAsk={() => askToWithdraw(row.transferId)}
              onCancel={cancelWithdraw}
              onWithdraw={() => withdraw(row.transferId)}
            />
          ))}
        </ul>
      )}
    </>
  );
}
